import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import {
    CrudListShell,
    ExpandableRow,
    ScheduleEmptyState,
    FilterBar,
    SearchInput,
    SortControl,
    ResultSummary,
    useListControls,
} from '../components/crud';

const SCHOOL_YEAR_SEARCH_FIELDS = ['school_year'];
const SCHOOL_YEAR_SORT_OPTIONS = [
    { key: 'school_year', label: 'School Year', accessor: (y) => y.school_year?.toLowerCase() ?? '' },
];

const SelectSchoolYearComponent = ({ currentSchoolYear, onUpdate }) => {
    const [schoolYearList, setSchoolYearList] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [selectedSchoolYear, setSelectedSchoolYear] = useState(currentSchoolYear);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalSchoolYear, setModalSchoolYear] = useState(null);

    const {
        items: visibleSchoolYears,
        resultCount,
        totalCount,
        isFiltered,
        search,
        sort,
        clear,
    } = useListControls(schoolYearList, {
        searchFields: SCHOOL_YEAR_SEARCH_FIELDS,
        sortOptions: SCHOOL_YEAR_SORT_OPTIONS,
    });

    useEffect(() => {
        fetchSchoolYears();
    }, []);

    const fetchSchoolYears = async () => {
        try {
            const response = await fetch(`${backendURL}/api/school-year-list`, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to fetch school years');
            }
            const data = await response.json();
            setSchoolYearList(data);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleEditClick = (year) => {
        setModalSchoolYear(year);
        setShowModal(true);
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setModalSchoolYear({ ...modalSchoolYear, [name]: value });
    };

    const handleModalUpdate = async () => {
        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${backendURL}/api/school-year-list/${modalSchoolYear.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(modalSchoolYear),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to update the school year');
            }

            await response.json();
            setSuccess('School year updated successfully!');
            fetchSchoolYears(); // Refresh the school year list
            setShowModal(false);
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const generateNextSchoolYear = () => {
        const existingYears = schoolYearList.map(year => year.school_year);
        const currentYear = new Date().getFullYear();
        let nextYearStart = currentYear;
        let nextYearEnd = currentYear + 1;
        let nextYear = `${nextYearStart}-${nextYearEnd}`;

        while (existingYears.includes(nextYear)) {
            nextYearStart++;
            nextYearEnd++;
            nextYear = `${nextYearStart}-${nextYearEnd}`;
        }

        return nextYear;
    };

    const handleCreateNewYear = async () => {
        const newSchoolYear = generateNextSchoolYear();

        const response = await fetch(`${backendURL}/api/school-year-list`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ school_year: newSchoolYear, active: false }),
            credentials: 'include'
        });

        if (response.ok) {
            await response.json();
            setSuccess('New school year created successfully!');
            fetchSchoolYears(); // Refresh the school year list
        } else {
            setError('Failed to create new school year');
        }
    };

    return (
        <div>
            <h3>Select Active School Year</h3>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            <Button variant="primary" className="m-1" onClick={handleCreateNewYear}>
                Create New School Year
            </Button>
            <p className="text-muted">
                This component allows you to set the current active school year for the Form Submission
                that everyone submits their ticket request to. This in turn controls which events are
                displayed to the user.
            </p>

            <CrudListShell
                showForm={false}
                tableHeading="School Years"
                filters={(
                    <FilterBar
                        summary={(
                            <ResultSummary
                                count={resultCount}
                                total={totalCount}
                                noun="school year"
                                onClear={clear}
                                isFiltered={isFiltered}
                            />
                        )}
                    >
                        <SearchInput label="Search school years" placeholder="School year" {...search} />
                        <SortControl {...sort} />
                    </FilterBar>
                )}
                emptyState={(
                    <tr>
                        <td>
                            <ScheduleEmptyState>No school years have been created yet.</ScheduleEmptyState>
                        </td>
                    </tr>
                )}
            >
                {visibleSchoolYears.map((year) => (
                    <ExpandableRow
                        key={year.id}
                        main={{ title: year.school_year }}
                        pills={[{ label: year.active ? 'Active' : 'Inactive', variant: year.active ? undefined : 'warning' }]}
                        actions={(
                            <Button size="sm" variant="secondary" onClick={() => handleEditClick(year)}>
                                <i className="bi bi-pencil"></i> Edit
                            </Button>
                        )}
                    />
                ))}
            </CrudListShell>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit School Year</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Label>{'School Year: '}</Form.Label>
                        <span>{` ${modalSchoolYear?.school_year}`}</span>
                        <Form.Group controlId="modalSchoolYearActive">
                            <Form.Label>Active</Form.Label>
                            <Form.Control
                                as="select"
                                name="active"
                                value={modalSchoolYear?.active || ''}
                                onChange={handleModalChange}
                            >
                                <option value={''}>Select status</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </Form.Control>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleModalUpdate} disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                                {' '}Loading...
                            </>
                        ) : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SelectSchoolYearComponent;
